import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { 
  Sparkles, 
  Gem, 
  Heart, 
  Star, 
  Hand,
  Eye,
  Scissors,
  Droplets,
  ArrowRight
} from "lucide-react";

const heroImage = "https://images.pexels.com/photos/11896310/pexels-photo-11896310.jpeg";
const nailImage = "https://images.pexels.com/photos/3997391/pexels-photo-3997391.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750";
const lashImage = "https://images.pexels.com/photos/3985329/pexels-photo-3985329.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750";
const hairImage = "https://images.pexels.com/photos/3993449/pexels-photo-3993449.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750";

const serviceHighlights = [
  { icon: Sparkles, title: "Facials", desc: "LED, High Frequency, Chemical & More", bookId: "facials" },
  { icon: Hand, title: "Nails", desc: "Manicure, Pedicure, GelX, Acrylic", bookId: "nails" },
  { icon: Eye, title: "Lashes", desc: "Classic & Volume Extensions", bookId: "lashes" },
  { icon: Droplets, title: "Waxing", desc: "Full Body Waxing Services", bookId: "waxing" },
  { icon: Scissors, title: "Hair", desc: "Color, Keratin, Silk Press", bookId: "hair" },
  { icon: Gem, title: "Wigs", desc: "Install, Color & Revamp", bookId: "hair" },
];

const courses = [
  { title: "Facial Training", price: "60,000", kit: "20,000", courseId: "facial" },
  { title: "Manicure & Pedicure", price: "45,000", kit: "15,000", courseId: "mani_pedi" },
  { title: "Acrylic Nails", price: "50,000", kit: "18,000", courseId: "acrylic" },
  { title: "Lash Extensions", price: "40,000", kit: "2,000", courseId: "lash" },
  { title: "Lash Lift", price: "45,000", kit: "Included", courseId: "lash_lift" },
  { title: "Body Waxing", price: "60,000", kit: "20,000", courseId: "waxing" },
  { title: "Hair Services", price: "55,000", kit: "20,000", courseId: "hair" },
  { title: "Advanced Hair", price: "60,000", kit: "40,000", courseId: "advanced_hair" },
  { title: "Brow Training", price: "40,000", kit: "20,000", courseId: "brow_training" },
  { title: "Wig Master Class", price: "80,000", kit: "20,000", courseId: "wig_masterclass" },
];

const AnimatedSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.8 }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export const HomePage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="hero-section" data-testid="hero-section">
        <div
          className="hero-bg"
          style={{ backgroundImage: `url(${heroImage})` }}
        />
        <div className="hero-overlay" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
          >
            <span className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059] mb-6 inline-block">
              The Only Salon/Spa of its Kind in Georgetown & Mahaica
            </span>
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.4 }}
            className="font-heading text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight text-white mb-6"
          >
            <span className="gold-gradient">Luxury Beauty</span>
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.6 }}
            className="font-body text-lg md:text-xl text-[#A3A3A3] max-w-2xl mx-auto mb-10"
          >
            Services & Training in Georgetown & Mahaica, Guyana. Experience luxury treatments 
            or learn to start your own beauty business.
          </motion.p>
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.8 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Link
              to="/book"
              data-testid="hero-cta-services"
              className="bg-[#C5A059] text-[#050505] font-semibold text-sm tracking-[0.15em] uppercase px-8 py-4 hover:brightness-110 transition-all duration-300"
            >
              Book a Service
            </Link>
            <Link
              to="/courses"
              data-testid="hero-cta-courses"
              className="border border-[#C5A059] text-[#C5A059] font-semibold text-sm tracking-[0.15em] uppercase px-8 py-4 hover:bg-[#C5A059]/10 transition-all duration-300"
            >
              View Courses
            </Link>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2"
        >
          <div className="w-6 h-10 border border-white/30 rounded-full flex justify-center">
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1 h-2 bg-[#C5A059] rounded-full mt-2"
            />
          </div>
        </motion.div>
      </section>

      {/* Services Overview */}
      <section className="py-24 md:py-32 bg-[#050505]" data-testid="services-overview">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimatedSection>
            <div className="text-center mb-16">
              <span className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059]">
                What We Offer
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light text-white mt-4">
                Our Services
              </h2>
              <div className="section-divider" />
              <p className="font-body text-[#A3A3A3] max-w-2xl mx-auto mt-6">
                The only salon/spa offering these services in Georgetown & Mahaica. 
                Experience luxury treatments by appointment.
              </p>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {serviceHighlights.map((service, index) => (
              <AnimatedSection key={service.title}>
                <Link 
                  to={`/book?service=${service.bookId}`}
                  className="bento-card p-8 text-center group block" 
                  data-testid={`service-highlight-${index}`}
                >
                  <div className="w-16 h-16 mx-auto mb-6 border border-[#C5A059] flex items-center justify-center group-hover:bg-[#C5A059]/10 transition-all duration-500">
                    <service.icon className="w-7 h-7 text-[#C5A059]" />
                  </div>
                  <h3 className="font-heading text-xl text-white mb-2">{service.title}</h3>
                  <p className="font-body text-sm text-[#A3A3A3] mb-3">{service.desc}</p>
                  <span className="font-body text-xs text-[#C5A059] tracking-wider uppercase opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    Book Now &rarr;
                  </span>
                </Link>
              </AnimatedSection>
            ))}
          </div>

          <AnimatedSection>
            <div className="text-center mt-12">
              <Link
                to="/services"
                className="inline-flex items-center gap-2 text-[#C5A059] font-body text-sm tracking-wider uppercase hover:gap-4 transition-all duration-300"
              >
                View All Services
                <ArrowRight size={16} />
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Training Section */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]" data-testid="training-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <AnimatedSection>
              <div>
                <span className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059]">
                  Professional Training
                </span>
                <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light text-white mt-4 mb-6">
                  Start Your Beauty Career
                </h2>
                <p className="font-body text-[#A3A3A3] leading-relaxed mb-6">
                  Want to learn? Our comprehensive training courses will teach you 
                  the skills to start your own beauty business. Hands-on training 
                  with professional kits included.
                </p>
                <p className="font-body text-[#A3A3A3] leading-relaxed mb-8">
                  All prices in Guyanese Dollars (GYD). Registration now open for 
                  April 2025 courses. Limited spaces available.
                </p>
                <Link
                  to="/courses"
                  data-testid="training-cta"
                  className="bg-[#C5A059] text-[#050505] font-semibold text-sm tracking-[0.15em] uppercase px-8 py-4 hover:brightness-110 transition-all duration-300 inline-block"
                >
                  View All Courses
                </Link>
              </div>
            </AnimatedSection>

            <AnimatedSection>
              <div className="bento-card p-8">
                <h3 className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059] mb-6">
                  Course Pricing (GYD)
                </h3>
                <div className="space-y-4">
                  {courses.map((course) => (
                    <Link
                      key={course.title}
                      to={`/courses#${course.courseId}`}
                      className="flex justify-between items-center pb-4 border-b border-[#262626] last:border-0 group cursor-pointer hover:bg-[#C5A059]/5 -mx-2 px-2 py-3 transition-all duration-300"
                      data-testid={`home-course-${course.courseId}`}
                    >
                      <div>
                        <span className="font-body text-white group-hover:text-[#C5A059] transition-colors">{course.title}</span>
                        <span className="block text-xs text-[#A3A3A3]">Kit: {course.kit.startsWith("$") || course.kit === "Included" ? course.kit : `$${course.kit}`}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-heading text-xl text-[#C5A059]">${course.price}</span>
                        <ArrowRight size={14} className="text-[#C5A059] opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Gallery Preview */}
      <section className="py-24 md:py-32 bg-[#050505]" data-testid="gallery-preview">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimatedSection>
            <div className="text-center mb-12">
              <span className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059]">
                Our Work
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light text-white mt-4">
                Gallery
              </h2>
            </div>
          </AnimatedSection>

          <AnimatedSection>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="aspect-square overflow-hidden">
                <img src={nailImage} alt="Nail services" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="aspect-square overflow-hidden">
                <img src={lashImage} alt="Lash services" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="aspect-square overflow-hidden hidden md:block">
                <img src={hairImage} alt="Hair services" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
              </div>
            </div>
          </AnimatedSection>

          <AnimatedSection>
            <div className="text-center mt-12">
              <Link
                to="/gallery"
                className="inline-flex items-center gap-2 text-[#C5A059] font-body text-sm tracking-wider uppercase hover:gap-4 transition-all duration-300"
              >
                View Full Gallery
                <ArrowRight size={16} />
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimatedSection>
            <div className="bento-card p-12 md:p-16 text-center">
              <span className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059]">
                Ready to Get Started?
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light text-white mt-4 mb-6">
                Book Now or Enroll Today
              </h2>
              <p className="font-body text-lg text-[#A3A3A3] max-w-2xl mx-auto mb-10">
                Whether you need a treatment or want to learn, we're here to help. 
                Contact us to book an appointment or register for a course.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contact"
                  data-testid="cta-contact"
                  className="bg-[#C5A059] text-[#050505] font-semibold text-sm tracking-[0.15em] uppercase px-8 py-4 hover:brightness-110 transition-all duration-300"
                >
                  Contact Us
                </Link>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
